import type { ErrorResponse } from '@/types';
import { AxiosError } from 'axios';

export const EHR_CONNECTION_ERROR_TYPE = 'EHRAPIConnectionError';

/**
 * The upstream EHR's own HTTP status, surfaced by the backend on the error
 * `detail`. This is distinct from `error.response?.status` (the status of our
 * own API call) — e.g. a wrapped Cerner `FhirError` can carry
 * `detail.ehr_status_code: 401` while our request resolves with a different
 * status. `401` means the EHR-launch token has expired.
 */
type EhrErrorDetail = ErrorResponse['detail'] & {
  ehr_status_code?: number | null;
  error_code?: string | null;
};

const EHR_TOKEN_EXPIRED_STATUS = 401;

function getEhrErrorDetail(error: AxiosError): EhrErrorDetail | undefined {
  return (error.response?.data as { detail?: EhrErrorDetail } | undefined)?.detail;
}

export function isEhrConnectionError(error: unknown): error is AxiosError {
  if (!(error instanceof AxiosError)) return false;
  if (error.response?.status !== 502) return false;
  const data = error.response?.data as ErrorResponse | undefined;
  return data?.detail?.type === EHR_CONNECTION_ERROR_TYPE;
}

/**
 * True when an EHR-launch request failed because the EHR session token has
 * expired (`detail.ehr_status_code === 401`). The user must relaunch Heidi
 * from the EHR to obtain a fresh token.
 */
export function isEhrTokenExpiredError(error: unknown): boolean {
  if (!(error instanceof AxiosError)) return false;
  const detail = getEhrErrorDetail(error);
  return detail?.ehr_status_code === EHR_TOKEN_EXPIRED_STATUS;
}

export function getEhrConnectionErrorMessage(error: AxiosError): string {
  const data = error.response?.data as ErrorResponse | undefined;
  return (
    data?.detail?.msg ??
    'The EHR service is temporarily unavailable. Please try again later.'
  );
}

export const EHR_RE_PAIR_REQUIRED_ERROR_TYPE = 'EHRRePairRequired';

// Additive EHRRePairRequired contract (ml-scribe ANZ-1192): HTTP 409 with
// detail.type === 'EHRRePairRequired'. Inert until the backend ships.
export function isEhrRePairRequiredError(error: unknown): error is AxiosError {
  if (!(error instanceof AxiosError)) return false;
  if (error.response?.status !== 409) return false;
  const data = error.response?.data as ErrorResponse | undefined;
  return data?.detail?.type === EHR_RE_PAIR_REQUIRED_ERROR_TYPE;
}

/**
 * Backend `detail.error_code` for a note push whose outcome is unknown.
 *
 * Some EHRs commit the note before they answer and routinely answer slower
 * than the read timeout, so an ambiguous transport failure usually means the
 * note *did* land. The backend raises a distinct error for that case rather
 * than reporting a flat failure, because the safe advice differs: check the
 * chart before retrying, instead of retrying straight away.
 *
 * Absent on every other failure, including EHR rejections, where we do know
 * nothing was written.
 */
export const NOTE_PUSH_UNCONFIRMED_ERROR_CODE = 'ECW_NOTE_PUSH_UNCONFIRMED';

/**
 * True when the backend could not confirm whether the note reached the EHR.
 *
 * Keyed on `error_code` rather than the exception name or `msg`: the code is
 * the stable contract the backend documents for clients to branch on, while
 * `msg` is translated and the type name is an implementation detail.
 */
export function isNotePushUnconfirmedError(error: unknown): boolean {
  if (!(error instanceof AxiosError)) return false;
  const detail = getEhrErrorDetail(error);
  return detail?.error_code === NOTE_PUSH_UNCONFIRMED_ERROR_CODE;
}

/**
 * Backend `detail.error_code` for a note push that sent nothing because no note
 * section is linked to a section the EHR accepts.
 *
 * Provider-neutral: any EHR can raise it, so one branch here covers them all.
 * Nothing reached the EHR, so a plain retry fails the same way. The clinician
 * has to link a section first, which is why this gets its own copy instead of
 * the generic retry toast.
 */
export const NO_LINKED_SECTIONS_ERROR_CODE = 'NO_LINKED_SECTIONS';

/**
 * True when a note push was rejected because no section was linked.
 *
 * Keyed on `error_code` for the same reason as `isNotePushUnconfirmedError`.
 */
export function isNoLinkedSectionsError(error: unknown): boolean {
  if (!(error instanceof AxiosError)) return false;
  const detail = getEhrErrorDetail(error);
  return detail?.error_code === NO_LINKED_SECTIONS_ERROR_CODE;
}

/**
 * Backend `detail.error_code` for a note push the EHR already holds. Nothing
 * was sent, because a second push would file a duplicate note.
 */
export const ALREADY_PUSHED_ERROR_CODE = 'ALREADY_PUSHED';

export function isAlreadyPushedError(error: unknown): boolean {
  if (!(error instanceof AxiosError)) return false;
  const detail = getEhrErrorDetail(error);
  return detail?.error_code === ALREADY_PUSHED_ERROR_CODE;
}
